import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { getAlerts } from '../lib/api';
import { 
  House, 
  WarningCircle, 
  Buildings, 
  Users, 
  Wrench, 
  Gear, 
  SignOut,
  MagnifyingGlass,
  List,
  X,
  Globe,
  EnvelopeSimple,
  Truck,
  Megaphone,
  Bell,
  BellSlash,
  FileText,
  ClockCounterClockwise,
  CalendarCheck,
  ClipboardText,
  Code,
  CurrencyCircleDollar,
  FilePdf,
  Newspaper,
  ChatsCircle,
  FolderOpen,
  Images,
  Crown,
  Icon
} from '@phosphor-icons/react';
import { NavLink } from './navigation/NavLink';
import { CategoryNav } from './navigation/CategoryNav';
import { Button } from './ui/button';
import { SearchModal } from './SearchModal';
import { isPushSupported, isSubscribed, subscribeToPush, unsubscribeFromPush } from '../lib/push';
import { toast } from 'sonner';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  icon: Icon;
  label: string;
  badge?: string;
  permission: string;
}

interface NavCategory {
  id: string;
  icon: Icon;
  label: string;
  permission: string;
  items: NavItem[];
}

interface Alerts {
  unreadMessages: number;
  urgentMessages: number;
  activeIssues: number;
  urgentIssues: number;
}

export function Layout({ children }: LayoutProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(true);
  
  // Alert counts for badges
  const [alerts, setAlerts] = useState<Alerts>({
    unreadMessages: 0,
    urgentMessages: 0,
    activeIssues: 0,
    urgentIssues: 0
  });

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!user || !['admin', 'board_member', 'superadmin'].includes(user.role)) return;
    try {
      const { data } = await getAlerts();
      setAlerts({
        unreadMessages: data.unread_messages || 0,
        urgentMessages: data.urgent_messages || 0,
        activeIssues: data.active_issues || 0,
        urgentIssues: data.urgent_issues || 0
      });
    } catch (e) {
      console.error('Error fetching alerts:', e);
    }
  }, [user]);

  // Poll for alerts every 10 seconds
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    const checkPush = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        try {
          const subscribed = await isSubscribed();
          setPushEnabled(subscribed);
        } catch (e) {
          console.error('Error checking push:', e);
        }
      }
      setPushLoading(false);
    };
    checkPush();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'sv' ? 'en' : 'sv';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const togglePush = async () => {
    if (!pushSupported) return;
    
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success('Notifikationer avstängda');
      } else {
        await subscribeToPush();
        setPushEnabled(true);
        toast.success('Notifikationer aktiverade');
      }
    } catch (error: unknown) {
      console.error('Push toggle error:', error);
      if (error instanceof Error && error.message?.includes('permission')) {
        toast.error('Tillåt notifikationer i webbläsaren');
      } else {
        toast.error('Kunde inte ändra notifikationer');
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Build nav categories based on permissions
  const navCategories: NavCategory[] = [
    {
      id: 'issues',
      icon: WarningCircle,
      label: 'Ärenden',
      permission: 'issues_view',
      items: [
        { path: '/issues', icon: WarningCircle, label: 'Alla ärenden', badge: 'issues', permission: 'issues_view' },
        { path: '/orders', icon: Wrench, label: 'Beställningar', permission: 'orders' },
        { path: '/suppliers', icon: Truck, label: 'Leverantörer', permission: 'suppliers' },
      ]
    },
    {
      id: 'communication',
      icon: ChatsCircle,
      label: 'Kommunikation',
      permission: 'email',
      items: [
        { path: '/mail', icon: EnvelopeSimple, label: 'Styrelsens mail', permission: 'board_email' },
        { path: '/email', icon: EnvelopeSimple, label: 'Meddelanden', badge: 'messages', permission: 'email' },
        { path: '/service-messages', icon: Megaphone, label: 'Servicemeddelande', permission: 'service_messages' },
      ]
    },
    {
      id: 'property',
      icon: Buildings,
      label: 'Fastigheter',
      permission: 'properties',
      items: [
        { path: '/properties', icon: Buildings, label: 'Fastigheter', permission: 'properties' },
        { path: '/apartments', icon: House, label: 'Lägenheter', permission: 'apartments' },
        { path: '/maintenance', icon: CalendarCheck, label: 'Underhåll', permission: 'properties' },
        { path: '/protocols', icon: ClipboardText, label: 'Protokoll', permission: 'properties' },
      ]
    },
    {
      id: 'members',
      icon: Users,
      label: 'Medlemmar',
      permission: 'users_view',
      items: [
        { path: '/residents', icon: Users, label: 'Användare', permission: 'users_view' },
        { path: '/history', icon: ClockCounterClockwise, label: 'Historik', permission: 'users_view' },
      ]
    },
    {
      id: 'economy',
      icon: CurrencyCircleDollar,
      label: 'Ekonomi',
      permission: 'properties',
      items: [
        { path: '/economy', icon: CurrencyCircleDollar, label: 'Översikt', permission: 'properties' },
        { path: '/invoicing', icon: FilePdf, label: 'Fakturering', permission: 'properties' },
        { path: '/annual-report', icon: FilePdf, label: 'Årsrapport', permission: 'properties' },
      ]
    },
    {
      id: 'documents',
      icon: FolderOpen,
      label: 'Dokument',
      permission: 'dashboard',
      items: [
        { path: '/documents', icon: FileText, label: 'Alla dokument', permission: 'dashboard' },
      ]
    },
    {
      id: 'admin',
      icon: Gear,
      label: 'Administration',
      permission: 'settings',
      items: [
        { path: '/settings', icon: Gear, label: 'Inställningar', permission: 'settings' },
        { path: '/portal-admin', icon: Globe, label: 'Webbportal', permission: 'settings' },
        { path: '/portal-content', icon: Images, label: 'Portalinnehåll', permission: 'settings' },
        { path: '/embed-form', icon: Code, label: 'Externt formulär', permission: 'settings' },
      ]
    }
  ];

  // Filter categories based on permissions
  const filteredCategories = navCategories
    .map(category => ({
      ...category,
      items: category.items.filter(item => hasPermission(item.permission))
    }))
    .filter(category => category.items.length > 0 && hasPermission(category.permission));

  // Dashboard is always at top (standalone)
  const dashboardItem: NavItem = { 
    path: '/dashboard', 
    icon: House, 
    label: t('dashboard'), 
    permission: 'dashboard' 
  };

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Auto-expand category containing current path
  useEffect(() => {
    const currentCategory = navCategories.find(cat => 
      cat.items.some(item => item.path === location.pathname)
    );
    if (currentCategory && !expandedCategories.includes(currentCategory.id)) {
      setExpandedCategories(prev => [...prev, currentCategory.id]);
    }
  }, [location.pathname]);

  // Callbacks for navigation components
  const handleMobileClose = useCallback(() => setMobileMenuOpen(false), []);
  const handleFetchAlerts = useCallback(() => fetchAlerts(), [fetchAlerts]);

  return (
    <div className="main-layout">
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex flex-col h-screen">
        {/* Header with app name and notification toggle */}
        <div className="p-6 border-b border-border flex-shrink-0">
          <h1 className="text-lg font-semibold tracking-tight text-slate-900">
            {t('app_name')}
          </h1>
          {/* Small notification toggle */}
          {pushSupported && (
            <button
              onClick={togglePush}
              disabled={pushLoading}
              className={`mt-2 flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                pushEnabled 
                  ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
              }`}
              data-testid="sidebar-push-toggle"
            >
              {pushLoading ? (
                <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
              ) : pushEnabled ? (
                <Bell size={12} weight="fill" />
              ) : (
                <BellSlash size={12} />
              )}
              <span>{pushEnabled ? 'Notiser på' : 'Notiser av'}</span>
            </button>
          )}
        </div>
        
        {/* Scrollable nav area */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Dashboard always at top */}
          {hasPermission('dashboard') && (
            <NavLink 
              item={dashboardItem} 
              alerts={alerts}
              onFetchAlerts={handleFetchAlerts}
            />
          )}
          
          {/* Divider */}
          <div className="h-px bg-slate-200 my-3" />
          
          {/* Category navigation */}
          {filteredCategories.map((category) => (
            <CategoryNav 
              key={category.id} 
              category={category} 
              alerts={alerts}
              isExpanded={expandedCategories.includes(category.id)}
              onToggle={toggleCategory}
              onFetchAlerts={handleFetchAlerts}
            />
          ))}
        </nav>
        
        {/* Fixed bottom section */}
        <div className="flex-shrink-0 border-t border-border bg-white">
          <div className="p-4 space-y-2">
            <button
              onClick={() => setSearchOpen(true)}
              className="nav-item w-full"
              data-testid="search-button"
            >
              <MagnifyingGlass size={20} weight="regular" />
              <span>{t('search')}</span>
              <kbd className="ml-auto text-xs bg-slate-100 px-1.5 py-0.5 rounded">⌘K</kbd>
            </button>
            
            <button
              onClick={toggleLanguage}
              className="nav-item w-full"
              data-testid="language-toggle"
            >
              <Globe size={20} weight="regular" />
              <span>{i18n.language === 'sv' ? 'English' : 'Svenska'}</span>
            </button>
            
            {user?.role === 'superadmin' && (
              <a
                href="/superadmin"
                className="nav-item w-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                data-testid="superadmin-link"
              >
                <Crown size={20} weight="fill" />
                <span>Superadmin</span>
              </a>
            )}
            
            <button
              onClick={handleLogout}
              className="nav-item w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="logout-button"
            >
              <SignOut size={20} weight="regular" />
              <span>{t('logout')}</span>
            </button>
          </div>
          
          {user && (
            <div className="p-4 border-t border-border">
              <div className="text-sm font-medium text-slate-900 truncate">{user.name}</div>
              <div className="text-xs text-slate-500 truncate">{user.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-border z-40">
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-base font-semibold text-slate-900">{t('app_name')}</h1>
          <div className="flex items-center gap-1">
            {/* Push notification toggle - mobile */}
            {pushSupported && (
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePush}
                disabled={pushLoading}
                className={`h-9 w-9 ${pushEnabled ? 'text-blue-600' : 'text-slate-400'}`}
                data-testid="mobile-push-toggle"
              >
                {pushEnabled ? <Bell size={18} weight="fill" /> : <BellSlash size={18} />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="h-9 w-9"
              data-testid="mobile-search-button"
            >
              <MagnifyingGlass size={18} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="h-9 w-9"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={18} /> : <List size={18} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-30 pt-14 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {/* Dashboard */}
            {hasPermission('dashboard') && (
              <NavLink 
                item={dashboardItem} 
                mobile 
                alerts={alerts}
                onMobileClose={handleMobileClose}
                onFetchAlerts={handleFetchAlerts}
              />
            )}
            
            {/* Divider */}
            <div className="h-px bg-slate-200 my-3" />
            
            {/* Categories */}
            {filteredCategories.map((category) => (
              <CategoryNav 
                key={category.id} 
                category={category} 
                mobile 
                alerts={alerts}
                isExpanded={expandedCategories.includes(category.id)}
                onToggle={toggleCategory}
                onMobileClose={handleMobileClose}
                onFetchAlerts={handleFetchAlerts}
              />
            ))}
            
            <hr className="my-4" />
            <button
              onClick={toggleLanguage}
              className="nav-item w-full"
            >
              <Globe size={20} weight="regular" />
              <span>{i18n.language === 'sv' ? 'English' : 'Svenska'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="nav-item w-full text-red-600"
            >
              <SignOut size={20} weight="regular" />
              <span>{t('logout')}</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content md:pt-0 pt-14">
        {children}
      </main>

      {/* Search Modal */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

export default Layout;
